import {API} from "../../../API/API";
import {BillingApiCurrencyListRequest, CurrencyEntity, PageModel} from "../../../proto/generated/api_entities_pb";
import {InitialPage} from "../../constans/pagination/pagination";

// Кэш для курсов валют
let currencyRatesCache: Map<number, number> | null = null;
// ID базовой валюты (rate=1 или isBase=true)
let baseCurrencyId: number | null = null;
// Информация о базовой валюте (ID и название)
let baseCurrencyInfo: { id: number; name: string } | null = null;
// Промис текущей загрузки — для дедупликации одновременных запросов
let loadCurrencyRatesPromise: Promise<Map<number, number>> | null = null;

/**
 * Загружает курсы валют из API и кэширует их
 * Экспортируем для прямого использования в хуках
 * Одновременные вызовы дедуплицируются — выполняется только один запрос
 */
export async function loadCurrencyRates(): Promise<Map<number, number>> {
  if (currencyRatesCache) {
    return currencyRatesCache;
  }

  if (loadCurrencyRatesPromise) {
    return loadCurrencyRatesPromise;
  }

  loadCurrencyRatesPromise = (async () => {
    try {
    const req = new BillingApiCurrencyListRequest()
      .setQuery('')
      .setPagemodel(
        new PageModel().setPage(InitialPage).setLimit(10000)
      );

    const res = await API.getCurrencyList(req);
    const rates = new Map<number, number>();

    let foundBaseCurrencyId: number | null = null;
    
    res.getCurrenciesList().forEach(currency => {
      const currencyId = currency.getCurrencyid();
      const rate = currency.getRate();
      const isBase = currency.getIsbase();
      const currencyName = currency.getName();
      
      if (currencyId && rate > 0) {
        rates.set(currencyId, rate);
      }
      
      // Находим базовую валюту (rate=1 или isBase=true)
      if (isBase || (rate === 1.0 && foundBaseCurrencyId === null)) {
        foundBaseCurrencyId = currencyId;
        // Сохраняем информацию о базовой валюте
        if (currencyId && currencyName) {
          baseCurrencyInfo = { id: currencyId, name: currencyName };
        }
      }
    });
    
    // Сохраняем ID базовой валюты в глобальную переменную
    baseCurrencyId = foundBaseCurrencyId;

    if (baseCurrencyId === null || !baseCurrencyInfo) {
      console.warn(`[loadCurrencyRates] Base currency not found!`);
    }

      currencyRatesCache = rates;
      return rates;
    } catch (error) {
      console.error('Failed to load currency rates:', error);
      return new Map<number, number>();
    } finally {
      loadCurrencyRatesPromise = null;
    }
  })();

  return loadCurrencyRatesPromise;
}

/**
 * Очищает кэш курсов валют (полезно при обновлении курсов)
 */
export function clearCurrencyRatesCache(): void {
  currencyRatesCache = null;
  baseCurrencyId = null;
  baseCurrencyInfo = null;
  loadCurrencyRatesPromise = null;
}

/**
 * Получает ID базовой валюты
 */
export function getBaseCurrencyId(): number | null {
  return baseCurrencyId;
}

/**
 * Получает информацию о базовой валюте (ID и название)
 */
export function getBaseCurrencyInfo(): { id: number; name: string } | null {
  return baseCurrencyInfo;
}

/**
 * Конвертирует сумму из одной валюты в другую
 * 
 * @param amount Сумма для конвертации
 * @param fromCurrencyId ID валюты источника
 * @param toCurrencyId ID валюты назначения
 * @returns Конвертированная сумма
 */
export async function convertCurrency(
  amount: number,
  fromCurrencyId: number,
  toCurrencyId: number
): Promise<number> {
  // Если валюты одинаковые, возвращаем сумму без изменений
  if (fromCurrencyId === toCurrencyId) {
    return amount;
  }

  // Если сумма равна 0 или отрицательная, возвращаем как есть
  if (amount <= 0) {
    return amount;
  }

  const rates = await loadCurrencyRates();
  const fromRate = rates.get(fromCurrencyId);
  const toRate = rates.get(toCurrencyId);

  // Если курсы не найдены, возвращаем исходную сумму
  if (!fromRate || !toRate || toRate <= 0) {
    console.warn(`Currency rates not found: from=${fromCurrencyId}, to=${toCurrencyId}`);
    return amount;
  }

  // Конвертация: amount_in_to_currency = amount_in_from_currency * (from_rate / to_rate)
  return amount * (fromRate / toRate);
}

/**
 * Синхронная версия конвертации (использует кэш, если он уже загружен)
 * Если кэш не загружен, возвращает исходную сумму
 * 
 * @param amount Сумма для конвертации
 * @param fromCurrencyId ID валюты источника
 * @param toCurrencyId ID валюты назначения
 * @returns Конвертированная сумма или исходная, если кэш не загружен
 */
export function convertCurrencySync(
  amount: number,
  fromCurrencyId: number,
  toCurrencyId: number
): number {
  // Если валюты одинаковые, возвращаем сумму без изменений
  if (fromCurrencyId === toCurrencyId) {
    return amount;
  }

  // Если сумма равна 0 или отрицательная, возвращаем как есть
  if (amount <= 0) {
    return amount;
  }

  // Если кэш не загружен, возвращаем исходную сумму
  if (!currencyRatesCache) {
    console.warn(`[convertCurrencySync] Currency rates cache not loaded. fromCurrencyId=${fromCurrencyId}, toCurrencyId=${toCurrencyId}`);
    return amount;
  }

  const fromRate = currencyRatesCache.get(fromCurrencyId);
  const toRate = currencyRatesCache.get(toCurrencyId);

  // Если курсы не найдены, возвращаем исходную сумму
  if (!fromRate || !toRate || toRate <= 0) {
    console.warn(`[convertCurrencySync] Currency rates not found: fromCurrencyId=${fromCurrencyId} (rate=${fromRate}), toCurrencyId=${toCurrencyId} (rate=${toRate})`);
    console.warn(`[convertCurrencySync] Available rates:`, Array.from(currencyRatesCache.entries()).map(([id, rate]) => `currencyId=${id}, rate=${rate}`).join(', '));
    return amount;
  }

  // Конвертация: amount_in_to_currency = amount_in_from_currency * (from_rate / to_rate)
  // Если fromRate = 1 (базовая валюта), toRate = 66.58 (EUR), то:
  // 88913471 * (1 / 66.58) = 88913471 / 66.58 ≈ 1,335,000 EUR
  const convertedAmount = amount * (fromRate / toRate);

  return convertedAmount;
}

/**
 * Получает курс валюты относительно базовой валюты
 * 
 * @param currencyId ID валюты
 * @returns Курс валюты (1.0 для базовой валюты)
 */
export async function getCurrencyRate(currencyId: number): Promise<number> {
  const rates = await loadCurrencyRates();
  return rates.get(currencyId) ?? 1.0;
}

/**
 * Синхронная версия получения курса валюты
 * 
 * @param currencyId ID валюты
 * @returns Курс валюты (1.0 для базовой валюты или если кэш не загружен)
 */
export function getCurrencyRateSync(currencyId: number): number {
  if (!currencyRatesCache) {
    return 1.0;
  }
  return currencyRatesCache.get(currencyId) ?? 1.0;
}


